import {
  Box,
  Checkbox,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useAnalyzeAudience } from 'client/modules/Analyze/AnalyzeAudienceContext';
import {
  cardSx,
  DETRACTOR,
  INK,
  MUTED,
  PASSIVE,
  PROMOTER,
  SECONDARY,
  sectionTitleSx,
  tableHeadCellSx,
} from 'client/modules/Analyze/analyzeTheme';
import OverTimeCompositionChart from 'client/modules/Analyze/components/OverTimeCompositionChart';
import OverTimeComparisonChart from 'client/modules/Analyze/components/OverTimeComparisonChart';
import ActivityIndicator from 'client/modules/App/components/ActivityIndicator/ActivityIndicator';
import SearchDateRangeControl from 'client/modules/Surveys/components/SearchDateRange/SearchDateRangeControl';
import { useAnalyzeEvolutionQuery } from 'client/modules/Surveys/surveyAnalyzeApiSlice';
import { startCase } from 'lodash';
import qs from 'qs';
import React, { useCallback, useMemo, useState } from 'react';
import { FaDownload } from 'react-icons/fa';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import Config from '../../../../server/config';

const messages = defineMessages({
  loadError: { id: 'analyze.overTime.loadError', defaultMessage: 'Failed to load evolution' },
  noData: { id: 'analyze.overTime.noData', defaultMessage: 'No responses in this period' },
  currentScore: { id: 'analyze.overTime.currentScore', defaultMessage: 'Current {label}' },
  responses: { id: 'analyze.overTime.responses', defaultMessage: 'Responses' },
  vsLast: { id: 'analyze.overTime.vsLast', defaultMessage: 'vs. {value} last {period}' },
  points: { id: 'analyze.overTime.points', defaultMessage: '{value} pts' },
  compositionTitle: {
    id: 'analyze.overTime.compositionTitle',
    defaultMessage: '{label} composition over time',
  },
  comparisonTitle: {
    id: 'analyze.overTime.comparisonTitle',
    defaultMessage: '{label} over time — comparison',
  },
  byTitle: { id: 'analyze.overTime.byTitle', defaultMessage: '{label} over time by {by}' },
  week: { id: 'analyze.group.week', defaultMessage: 'Week' },
  month: { id: 'analyze.group.month', defaultMessage: 'Month' },
  quarter: { id: 'analyze.group.quarter', defaultMessage: 'Quarter' },
  year: { id: 'analyze.group.year', defaultMessage: 'Year' },
  colChange: { id: 'analyze.overTime.colChange', defaultMessage: 'Change' },
  colTotal: { id: 'analyze.overTime.colTotal', defaultMessage: 'Total' },
  allResponses: { id: 'analyze.allResponses', defaultMessage: 'All responses' },
  allTime: { id: 'SearchDateRange.AllTime', defaultMessage: 'All time' },
  compareSeries: { id: 'analyze.overTime.compareSeries', defaultMessage: 'Compare series' },
  seriesCount: {
    id: 'analyze.overTime.seriesCount',
    defaultMessage: '{count, plural, one {# series} other {# series}}',
  },
  breakdownBy: { id: 'analyze.breakdown.dimension', defaultMessage: 'Break down by' },
  breakdownNone: { id: 'analyze.overTime.breakdownNone', defaultMessage: 'None' },
  groups: { id: 'analyze.breakdown.groups', defaultMessage: 'Groups' },
  tags: { id: 'analyze.breakdown.tags', defaultMessage: 'Tags' },
  properties: { id: 'analyze.breakdown.properties', defaultMessage: 'Properties' },
  promoters: { id: 'analyze.overTime.promoters', defaultMessage: 'Promoters' },
  passives: { id: 'analyze.overTime.passives', defaultMessage: 'Passives' },
  detractors: { id: 'analyze.overTime.detractors', defaultMessage: 'Detractors' },
  agree: { id: 'analyze.overTime.agree', defaultMessage: 'Agree' },
  neutral: { id: 'analyze.overTime.neutral', defaultMessage: 'Neutral' },
  disagree: { id: 'analyze.overTime.disagree', defaultMessage: 'Disagree' },
  satisfied: { id: 'analyze.overTime.satisfied', defaultMessage: 'Satisfied' },
  dissatisfied: { id: 'analyze.overTime.dissatisfied', defaultMessage: 'Dissatisfied' },
  positive: { id: 'analyze.overTime.positive', defaultMessage: 'Positive' },
  negative: { id: 'analyze.overTime.negative', defaultMessage: 'Negative' },
});

const GROUP_BYS = ['week', 'month', 'quarter', 'year'];

const COMP_LABEL_KEYS = {
  nps: { positive: 'promoters', neutral: 'passives', negative: 'detractors' },
  ces: { positive: 'agree', neutral: 'neutral', negative: 'disagree' },
  csat: { positive: 'satisfied', neutral: 'neutral', negative: 'dissatisfied' },
  star_five: { positive: 'positive', neutral: 'neutral', negative: 'negative' },
};

const SCORE_LABELS = { nps: 'NPS', ces: 'CES', csat: 'CSAT', star_five: 'Score' };

// Distinct line colors for compared/broken-down series.
const SERIES_PALETTE = [
  '#2E7CF6',
  '#E8A93C',
  '#1FB573',
  '#7A5AF8',
  '#EE5A52',
  '#1FB9A6',
  '#B07A12',
  '#5FC98C',
];

const selectSx = {
  backgroundColor: 'white',
  '& .MuiSelect-select': { py: 1.5, minWidth: 120 },
};

function scoreValue(series) {
  if (series.score != null) {
    return Math.round(series.score);
  }
  return series.average != null ? Math.round(series.average) : 0;
}

function KpiCard({ children }) {
  return <Box sx={cardSx}>{children}</Box>;
}

function GroupKpi({ label, color, pct, count, formatNumber }) {
  return (
    <KpiCard>
      <Typography sx={{ ...tableHeadCellSx }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.75 }}>
        <Typography
          sx={{ fontSize: 40, fontWeight: 800, color, letterSpacing: '-1px', lineHeight: 1 }}
        >
          {formatNumber(pct)}%
        </Typography>
        <Typography sx={{ fontSize: 15, color: MUTED }}>{formatNumber(count)}</Typography>
      </Box>
      <Box
        sx={{ height: 6, borderRadius: '3px', bgcolor: '#EEF1F5', mt: 1.25, overflow: 'hidden' }}
      >
        <Box
          sx={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', bgcolor: color }}
        />
      </Box>
    </KpiCard>
  );
}

function ChartLegend({ items, onToggle }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.25,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
    >
      {items.map(item => {
        const interactive = !!onToggle;
        return (
          <Box
            key={item.key}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : undefined}
            onClick={interactive ? () => onToggle(item.key) : undefined}
            onKeyDown={
              interactive
                ? e => (e.key === 'Enter' || e.key === ' ') && onToggle(item.key)
                : undefined
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.9,
              cursor: interactive ? 'pointer' : 'default',
              userSelect: 'none',
              opacity: item.hidden ? 0.4 : 1,
              transition: 'opacity .2s',
            }}
          >
            <Box
              sx={{
                width: item.line ? 16 : 12,
                height: item.line ? 3 : 12,
                borderRadius: item.line ? '2px' : '3px',
                bgcolor: item.color,
              }}
            />
            <Typography sx={{ fontSize: 13, color: SECONDARY, fontWeight: 500 }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function AnalyzeOverTimePage() {
  const { survey } = useOutletContext();
  const { dateRange, setDateRange, searches } = useAnalyzeAudience();
  const { formatMessage, formatNumber, formatDate } = useIntl();
  const [groupBy, setGroupBy] = useState('month');
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());
  const [searchParams, setSearchParams] = useSearchParams();

  const toggleSeries = useCallback(key => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const setSeriesParam = useCallback(
    values => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          const ids = (values || []).filter(Boolean);
          if (!ids.length || (ids.length === 1 && ids[0] === 'all')) {
            next.delete('series');
          } else {
            next.set('series', ids.join(','));
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setBreakdownParam = useCallback(
    value => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (!value || value === 'none') {
            next.delete('overTimeBreakdown');
          } else {
            next.set('overTimeBreakdown', value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const surveyType = survey?.surveyType || 'nps';

  const propertyOptions = useMemo(() => {
    const types = survey?.person_properties_types || {};
    return Object.entries(types)
      .filter(([, { hidden }]) => !hidden)
      .map(([key]) => ({ key, label: startCase(key) }))
      .sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));
  }, [survey?.person_properties_types]);

  const breakdownBy = searchParams.get('overTimeBreakdown') || 'none';
  const compareSeries = useMemo(() => {
    const raw = searchParams.get('series');
    const ids = raw ? raw.split(',').filter(Boolean) : [];
    return ids.length ? ids : ['all'];
  }, [searchParams]);

  // Breakdown applies on top of a single base filter (the one selected series).
  // It is only blocked when comparing several series at once.
  const breakdownAllowed = compareSeries.length <= 1;
  const breakdownActive = breakdownBy !== 'none' && breakdownAllowed;
  const breakdownDimension = breakdownBy.startsWith('property:') ? 'property' : breakdownBy;
  const breakdownPropertyKey = breakdownBy.startsWith('property:')
    ? breakdownBy.slice('property:'.length)
    : undefined;

  // A single series with no breakdown shows the full composition page scoped to
  // that filter; multiple series (or a breakdown) render comparison lines.
  const compositionKey = compareSeries[0];
  const showComposition = !breakdownActive && compareSeries.length === 1;

  const query = useMemo(
    () => ({
      survey: survey?.id,
      dateRange,
      groupBy,
      filters: compareSeries,
      ...(breakdownActive
        ? { breakdown: breakdownDimension, property_key: breakdownPropertyKey }
        : {}),
    }),
    [
      survey?.id,
      dateRange,
      groupBy,
      compareSeries,
      breakdownActive,
      breakdownDimension,
      breakdownPropertyKey,
    ],
  );

  const { data, isFetching, isError } = useAnalyzeEvolutionQuery(query, { skip: !survey?.id });
  const rows = useMemo(() => data?.rows || [], [data]);

  const shortLabel = useCallback(
    date => {
      if (groupBy === 'year') return String(date.getFullYear());
      if (groupBy === 'quarter') return `Q${Math.floor(date.getMonth() / 3) + 1}`;
      if (groupBy === 'week') return formatDate(date, { day: 'numeric', month: 'short' });
      return formatDate(date, { month: 'short' });
    },
    [groupBy, formatDate],
  );

  const longLabel = useCallback(
    date => {
      if (groupBy === 'year') return String(date.getFullYear());
      if (groupBy === 'quarter')
        return `Q${Math.floor(date.getMonth() / 3) + 1} ${date.getFullYear()}`;
      if (groupBy === 'week')
        return formatDate(date, { day: 'numeric', month: 'short', year: 'numeric' });
      return formatDate(date, { month: 'short', year: 'numeric' });
    },
    [groupBy, formatDate],
  );

  if (!survey) {
    return null;
  }

  const compKeys = COMP_LABEL_KEYS[surveyType] || COMP_LABEL_KEYS.nps;
  const scoreLabel = SCORE_LABELS[surveyType] || 'Score';
  const periodLabel = formatMessage(messages[groupBy]);

  const seriesName = id =>
    id === 'all'
      ? formatMessage(messages.allResponses)
      : searches?.find(s => s._id === id)?.name || id;

  const breakdownLabel = breakdownBy.startsWith('property:')
    ? propertyOptions.find(p => p.key === breakdownPropertyKey)?.label || breakdownPropertyKey
    : breakdownDimension === 'group'
      ? formatMessage(messages.groups)
      : breakdownDimension === 'tags'
        ? formatMessage(messages.tags)
        : '';

  // ---- composition mode data ----
  const periods = showComposition
    ? rows.map(r => ({ date: r.key, ...(r[compositionKey] || {}) })).filter(p => p.total > 0)
    : [];

  const compositionBuckets = periods.map(p => {
    const share = c => (p.total > 0 ? Math.round((c / p.total) * 1000) / 10 : 0);
    return {
      label: shortLabel(p.date),
      nps: scoreValue(p),
      total: p.total,
      segments: [
        {
          label: formatMessage(messages[compKeys.positive]),
          color: PROMOTER,
          pct: share(p.positive),
          count: p.positive,
        },
        {
          label: formatMessage(messages[compKeys.neutral]),
          color: PASSIVE,
          pct: share(p.neutral),
          count: p.neutral,
        },
        {
          label: formatMessage(messages[compKeys.negative]),
          color: DETRACTOR,
          pct: share(p.negative),
          count: p.negative,
        },
      ],
    };
  });

  const totals = periods.reduce(
    (acc, p) => ({
      pos: acc.pos + p.positive,
      neu: acc.neu + p.neutral,
      neg: acc.neg + p.negative,
      tot: acc.tot + p.total,
    }),
    { pos: 0, neu: 0, neg: 0, tot: 0 },
  );
  const last = periods[periods.length - 1];
  const prev = periods[periods.length - 2];
  const lastScore = last ? scoreValue(last) : null;
  const delta = last && prev ? lastScore - scoreValue(prev) : null;
  const pct = (count, total) => (total > 0 ? Math.round((count / total) * 1000) / 10 : 0);

  // ---- comparison / breakdown mode data ----
  const compareList = breakdownActive
    ? (data?.series || []).map((s, i) => ({
        key: s.key,
        label: s.label,
        color: SERIES_PALETTE[i % SERIES_PALETTE.length],
      }))
    : compareSeries.map((id, i) => ({
        key: id,
        label: seriesName(id),
        color: id === 'all' ? '#2E3A47' : SERIES_PALETTE[i % SERIES_PALETTE.length],
      }));

  const comparisonBuckets = rows.map(r => ({
    label: shortLabel(r.key),
    values: compareList.reduce((acc, s) => {
      const cell = r[s.key];
      acc[s.key] = cell && cell.total > 0 ? { score: scoreValue(cell), total: cell.total } : null;
      return acc;
    }, {}),
  }));
  const hasComparisonData = comparisonBuckets.some(b =>
    compareList.some(s => b.values[s.key] != null),
  );

  // Legend toggling: hide lines while keeping at least one visible.
  const visibleCompareList = compareList.filter(s => !hiddenSeries.has(s.key));
  const chartSeries = visibleCompareList.length ? visibleCompareList : compareList;

  const chartTitle = showComposition
    ? compositionKey === 'all'
      ? formatMessage(messages.compositionTitle, { label: scoreLabel })
      : `${seriesName(compositionKey)} · ${formatMessage(messages.compositionTitle, { label: scoreLabel })}`
    : breakdownActive
      ? formatMessage(messages.byTitle, { label: scoreLabel, by: breakdownLabel })
      : formatMessage(messages.comparisonTitle, { label: scoreLabel });

  const { timeZone } = Intl.DateTimeFormat().resolvedOptions();
  const downloadName = `analysis-${new Date().toISOString().substring(0, 10)}`;
  const downloadUrl = `${Config.apiUrl}/survey-analyze/${survey.id}/${downloadName}.xlsx?${qs.stringify(
    {
      groupBy,
      filters: breakdownActive ? ['all'] : compareSeries,
      dateRange,
      timeZone,
      field: 'score',
    },
  )}`;

  const dataReady = showComposition ? periods.length > 0 : hasComparisonData;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap', mb: 1 }}>
        <SearchDateRangeControl
          field="created_at"
          title={<FormattedMessage id="analyze.dateRange" defaultMessage="Date range" />}
          onChange={setDateRange}
          value={dateRange}
          emptyValue={formatMessage(messages.allTime)}
          alwaysOpen
        />
        <Select value={groupBy} onChange={e => setGroupBy(e.target.value)} sx={selectSx}>
          {GROUP_BYS.map(g => (
            <MenuItem key={g} value={g}>
              {formatMessage(messages[g])}
            </MenuItem>
          ))}
        </Select>

        <Select
          multiple
          value={compareSeries}
          onChange={e => setSeriesParam(e.target.value)}
          displayEmpty
          sx={selectSx}
          renderValue={selected =>
            selected.length === 1
              ? seriesName(selected[0])
              : formatMessage(messages.seriesCount, { count: selected.length })
          }
        >
          <MenuItem value="all">
            <Checkbox checked={compareSeries.includes('all')} />
            <ListItemText primary={formatMessage(messages.allResponses)} />
          </MenuItem>
          {searches?.length > 0 && (
            <ListSubheader>
              <FormattedMessage id="analyze.savedSearches" defaultMessage="Saved searches" />
            </ListSubheader>
          )}
          {searches?.map(s => (
            <MenuItem key={s._id} value={s._id}>
              <Checkbox checked={compareSeries.includes(s._id)} />
              <ListItemText primary={s.name} />
            </MenuItem>
          ))}
        </Select>

        <Select
          value={breakdownBy}
          onChange={e => setBreakdownParam(e.target.value)}
          disabled={!breakdownAllowed}
          sx={selectSx}
          renderValue={value => (
            <span>
              <Box component="span" sx={{ color: MUTED, mr: 0.75 }}>
                {formatMessage(messages.breakdownBy)}
              </Box>
              {value === 'none'
                ? formatMessage(messages.breakdownNone)
                : value === 'group'
                  ? formatMessage(messages.groups)
                  : value === 'tags'
                    ? formatMessage(messages.tags)
                    : propertyOptions.find(p => `property:${p.key}` === value)?.label || value}
            </span>
          )}
        >
          <MenuItem value="none">{formatMessage(messages.breakdownNone)}</MenuItem>
          <MenuItem value="group">{formatMessage(messages.groups)}</MenuItem>
          <MenuItem value="tags">{formatMessage(messages.tags)}</MenuItem>
          {propertyOptions.length > 0 && (
            <ListSubheader>{formatMessage(messages.properties)}</ListSubheader>
          )}
          {propertyOptions.map(({ key, label }) => (
            <MenuItem key={key} value={`property:${key}`}>
              {label}
            </MenuItem>
          ))}
        </Select>

        <Box
          component="a"
          href={downloadUrl}
          download
          title="Download .xlsx"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 46,
            height: 46,
            borderRadius: '10px',
            color: SECONDARY,
            backgroundColor: 'white',
            border: '1px solid #E2E7EE',
            '&:hover': { borderColor: '#C7CFDA', backgroundColor: 'rgba(0,0,0,0.02)' },
          }}
        >
          <FaDownload />
        </Box>
      </Box>

      {isFetching && !data && <ActivityIndicator />}
      {isError && !data && (
        <Typography color="error">{formatMessage(messages.loadError)}</Typography>
      )}
      {data && !dataReady && !isFetching && (
        <Typography>{formatMessage(messages.noData)}</Typography>
      )}

      {/* ===== Comparison / breakdown mode ===== */}
      {!showComposition && dataReady && (
        <Box sx={cardSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography sx={sectionTitleSx}>{chartTitle}</Typography>
            <ChartLegend
              items={compareList.map(s => ({
                key: s.key,
                label: s.label,
                color: s.color,
                line: true,
                hidden: hiddenSeries.has(s.key),
              }))}
              onToggle={toggleSeries}
            />
          </Box>
          <OverTimeComparisonChart
            buckets={comparisonBuckets}
            series={chartSeries}
            formatNumber={formatNumber}
          />
        </Box>
      )}

      {/* ===== Composition mode ===== */}
      {showComposition && dataReady && (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
              gap: 2.25,
              mb: 2.25,
            }}
          >
            <KpiCard>
              <Typography sx={tableHeadCellSx}>
                {formatMessage(messages.currentScore, { label: scoreLabel })}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.25, mt: 0.75 }}>
                <Typography
                  sx={{
                    fontSize: 40,
                    fontWeight: 800,
                    color: INK,
                    letterSpacing: '-1px',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatNumber(lastScore)}
                </Typography>
                {delta != null && delta !== 0 && (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      fontSize: 13,
                      fontWeight: 700,
                      color: delta > 0 ? '#1AAE6F' : '#D6453C',
                      bgcolor: delta > 0 ? '#E6F7EF' : '#FCEBEA',
                      px: 1,
                      py: 0.4,
                      borderRadius: '6px',
                    }}
                  >
                    {delta > 0 ? '▲' : '▼'}{' '}
                    {formatMessage(messages.points, { value: formatNumber(Math.abs(delta)) })}
                  </Box>
                )}
              </Box>
              {prev && (
                <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>
                  {formatMessage(messages.vsLast, {
                    value: formatNumber(scoreValue(prev)),
                    period: periodLabel.toLowerCase(),
                  })}
                </Typography>
              )}
            </KpiCard>

            <KpiCard>
              <Typography sx={tableHeadCellSx}>{formatMessage(messages.responses)}</Typography>
              <Typography
                sx={{
                  fontSize: 40,
                  fontWeight: 800,
                  color: INK,
                  letterSpacing: '-1px',
                  mt: 0.75,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatNumber(totals.tot)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.75 }}>
                {`${longLabel(periods[0].date)} – ${longLabel(last.date)}`}
              </Typography>
            </KpiCard>

            <GroupKpi
              label={formatMessage(messages[compKeys.positive])}
              color={PROMOTER}
              pct={pct(totals.pos, totals.tot)}
              count={totals.pos}
              formatNumber={formatNumber}
            />
            <GroupKpi
              label={formatMessage(messages[compKeys.negative])}
              color={DETRACTOR}
              pct={pct(totals.neg, totals.tot)}
              count={totals.neg}
              formatNumber={formatNumber}
            />
          </Box>

          <Box sx={{ ...cardSx, mb: 2.25 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography sx={sectionTitleSx}>{chartTitle}</Typography>
              <ChartLegend
                items={[
                  {
                    key: 'pos',
                    label: formatMessage(messages[compKeys.positive]),
                    color: PROMOTER,
                  },
                  { key: 'neu', label: formatMessage(messages[compKeys.neutral]), color: PASSIVE },
                  {
                    key: 'neg',
                    label: formatMessage(messages[compKeys.negative]),
                    color: DETRACTOR,
                  },
                  { key: 'score', label: scoreLabel, color: INK, line: true },
                ]}
              />
            </Box>
            <OverTimeCompositionChart
              buckets={compositionBuckets}
              scoreLabel={scoreLabel}
              formatNumber={formatNumber}
            />
          </Box>

          <Box sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1.2fr 1fr 1fr 1.4fr 1.4fr 1.4fr 1fr',
                px: 3.25,
                py: 1.75,
                bgcolor: '#F8FAFC',
                borderBottom: '1px solid #ECEFF3',
                ...tableHeadCellSx,
              }}
            >
              <span>{periodLabel}</span>
              <span style={{ textAlign: 'right' }}>{scoreLabel}</span>
              <span style={{ textAlign: 'right' }}>{formatMessage(messages.colChange)}</span>
              <span style={{ textAlign: 'right' }}>
                {formatMessage(messages[compKeys.positive])}
              </span>
              <span style={{ textAlign: 'right' }}>
                {formatMessage(messages[compKeys.neutral])}
              </span>
              <span style={{ textAlign: 'right' }}>
                {formatMessage(messages[compKeys.negative])}
              </span>
              <span style={{ textAlign: 'right' }}>{formatMessage(messages.colTotal)}</span>
            </Box>
            {periods.map((p, i) => {
              const score = scoreValue(p);
              const rowDelta = i > 0 ? score - scoreValue(periods[i - 1]) : null;
              return (
                <Box
                  key={p.date.toISOString()}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1.2fr 1fr 1fr 1.4fr 1.4fr 1.4fr 1fr',
                    px: 3.25,
                    py: 1.75,
                    borderBottom: '1px solid #F1F4F8',
                    fontSize: 14,
                    color: INK,
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ fontWeight: 600 }}>{longLabel(p.date)}</Box>
                  <Box
                    sx={{ textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(score)}
                  </Box>
                  <Box
                    sx={{
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      color:
                        rowDelta == null || rowDelta === 0
                          ? MUTED
                          : rowDelta > 0
                            ? '#1AAE6F'
                            : '#D6453C',
                    }}
                  >
                    {rowDelta == null
                      ? '—'
                      : `${rowDelta > 0 ? '▲' : rowDelta < 0 ? '▼' : ''} ${formatNumber(Math.abs(rowDelta))}`}
                  </Box>
                  <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(pct(p.positive, p.total))}%
                  </Box>
                  <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(pct(p.neutral, p.total))}%
                  </Box>
                  <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatNumber(pct(p.negative, p.total))}%
                  </Box>
                  <Box
                    sx={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {formatNumber(p.total)}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

export default AnalyzeOverTimePage;
